import * as shared from "@/styles/shared";
import api from "@shared/api/client";
import { isDecimalInputValid, parseDecimalInput } from "@shared/utils";
import { Clock } from "lucide-react";
import { useEffect, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import * as settingsStyles from "../../pages/SettingsPage.styles";
import * as styles from "./ScrapingScheduleSettings.styles";

const ScrapingScheduleSettings = () => {
  const { t } = useTranslation("settings");
  const [scrapingTime, setScrapingTime] = useState<string>("");
  const [minInvoiceAmount, setMinInvoiceAmount] = useState<string>("");
  const [hasSchedule, setHasSchedule] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.scrapingSchedule
      .get()
      .then((res) => {
        if (res.scrapingTime) {
          setScrapingTime(res.scrapingTime);
          setHasSchedule(true);
        } else {
          setHasSchedule(false);
        }
        if (res.minInvoiceAmount != null) setMinInvoiceAmount(String(res.minInvoiceAmount));
        setLoading(false);
      })
      .catch(() => {
        setError(t("scrapingLoadError"));
        setLoading(false);
      });
  }, []);

  const handleMinAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isDecimalInputValid(value)) setMinInvoiceAmount(value);
  };

  const handleSave = () => {
    setSaving(true);
    api.scrapingSchedule
      .update(scrapingTime.trim() || null, parseDecimalInput(minInvoiceAmount))
      .then((res) => {
        setHasSchedule(!!res.scrapingTime);
        setSaving(false);
      })
      .catch((err) => {
        setError(err.message ?? t("scrapingUpdateError"));
        setSaving(false);
      });
  };

  const handleClear = () => {
    setScrapingTime("");
    setHasSchedule(false);
    api.scrapingSchedule.update(null).catch((err) => setError(err.message ?? t("scrapingDisableError")));
  };

  if (loading) return <p className={shared.muted}>{t("scrapingLoading")}</p>;

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

  return (
    <div className={styles.container}>
      <div className={styles.timePicker}>
        <div className={shared.rowGap2}>
          <Clock className={styles.timePickerIcon} />
          <select
            value={scrapingTime.split(":")[0] || ""}
            onChange={(e) => setScrapingTime(`${e.target.value}:${scrapingTime.split(":")[1] ?? "00"}`)}
            className={styles.timeSelect}
          >
            <option value="" disabled>
              --
            </option>
            {hours.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>

          <span className={styles.timeSeparator}>:</span>

          <select
            value={scrapingTime.split(":")[1] || ""}
            onChange={(e) => setScrapingTime(`${scrapingTime.split(":")[0] ?? "00"}:${e.target.value}`)}
            className={styles.timeSelect}
          >
            <option value="" disabled>
              --
            </option>
            {minutes.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        {hasSchedule && (
          <span className={styles.scheduleStatus}>
            <span className={styles.scheduleStatusDot} />
            {t("scheduleActive")}
          </span>
        )}
      </div>

      <div>
        <label className={styles.formLabel}>{t("minInvoiceAmount")}</label>
        <p className={shared.muted}>{t("minInvoiceAmountDescription")}</p>
        <input
          value={minInvoiceAmount}
          onChange={handleMinAmountChange}
          placeholder={t("minInvoiceAmountPlaceholder")}
          type="text"
          inputMode="decimal"
          className={styles.amountInput}
        />
      </div>

      <div className={styles.buttonGroup}>
        <button onClick={handleSave} disabled={saving} className={settingsStyles.saveButton}>
          {saving ? t("scrapingSaving") : t("scrapingSave")}
        </button>

        {hasSchedule && (
          <button onClick={handleClear} className={settingsStyles.cancelButton}>
            {t("scrapingDisable")}
          </button>
        )}
      </div>

      {error && <p className={styles.errorText}>{error}</p>}
    </div>
  );
};

export { ScrapingScheduleSettings };
